import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { DraftingCompass, FileLock2 } from "lucide-react";

const VAULT_IMG = "https://images.unsplash.com/photo-1582139329536-e7284fece509?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBiYW5rJTIwdmF1bHQlMjBzZWN1cml0eXxlbnwwfHx8fDE3ODc5OTMyMDB8MA&ixlib=rb-4.1.0&q=85";

const Philosophy = ({ profile }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  if (!profile) return null;

  return (
    <section id="philosophy" ref={ref} className="relative py-24 md:py-32 lg:py-48 overflow-hidden" data-testid="philosophy-section">
      <motion.div className="absolute inset-0" style={{ y }}>
        <img src={VAULT_IMG} alt="Bank vault door" className="w-full h-[120%] object-cover opacity-25" />
      </motion.div>
      <div className="absolute inset-0 bg-black/70" />

      <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
        <motion.p
          className="font-mono text-xs tracking-[0.3em] text-emerald-400 mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          data-testid="philosophy-chapter-label"
        >
          CHAPTER 00 — THE PHILOSOPHY
        </motion.p>

        <motion.blockquote
          className="font-display text-3xl sm:text-4xl lg:text-5xl italic leading-snug text-gray-100 mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          data-testid="philosophy-quote"
        >
          “{profile.philosophy}”
        </motion.blockquote>

        <motion.p
          className="text-gray-400 max-w-2xl mx-auto leading-relaxed mb-20"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          viewport={{ once: true }}
          data-testid="philosophy-detail"
        >
          {profile.philosophyDetail}
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10 text-left">
          <motion.div
            className="bg-[#0a0a0a] p-8 md:p-10 hover:bg-[#0d0d0d] transition-colors"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="philosophy-card-design"
          >
            <DraftingCompass className="h-6 w-6 text-emerald-400 mb-6" />
            <h3 className="font-mono text-sm tracking-widest uppercase text-gray-100 mb-4">Security by Design</h3>
            <p className="text-sm text-gray-400 leading-relaxed">
              Threat modelling before the first VM is provisioned. Zero Trust, least-privilege and
              defence-in-depth designed into every network segment and cloud workload — never bolted on after.
            </p>
          </motion.div>
          <motion.div
            className="bg-[#0a0a0a] p-8 md:p-10 hover:bg-[#0d0d0d] transition-colors"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            viewport={{ once: true }}
            data-testid="philosophy-card-policy"
          >
            <FileLock2 className="h-6 w-6 text-emerald-400 mb-6" />
            <h3 className="font-mono text-sm tracking-widest uppercase text-gray-100 mb-4">Security by Policy</h3>
            <p className="text-sm text-gray-400 leading-relaxed">
              Every user, system and vendor operates within a governed, auditable boundary. 20+ policies
              authored from scratch across three organizations — with years of zero audit findings to show for it.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Philosophy;
